import useAppInstallRequiredModal from '@/hooks/cuj/useAppInstallRequiredModal';
import useInfiniteSearchNonF2fDoctors from '@/hooks/search/useInfiniteSearchNonF2fDoctors';
import useCustomRouter from '@/hooks/useCustomRouter';
import { EDeprecatedCategoryType } from '@/services/doctor/DiagnosisCategoryService';
import {
  DoctorSortType,
  DoctorType,
  MedicalCategoryType,
  TelemedicineEligibleTargetType,
} from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { EIntegratedSearchTabType, ERequestButtonType, NonF2fDoctorVo } from '@/types/search';
import { coreLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { useRouter } from 'next/router';

interface Props {
  homeSearchCategory: { category: MedicalCategoryType; id: string } | null;
  keyword: string;
  tab: EIntegratedSearchTabType;
}

export interface SearchDoctorViewModel {
  keyword: string;
  doctors: Array<NonF2fDoctorVo>;
  isLoading: boolean;
  hasNextPage: boolean;
  onClickDoctor: (doctorPid: string) => void;
  handleClickRequestButton: () => void;
  fetchNextPage: () => void;
  moveToCategoryListScreen: () => void;
}

const useSearchDoctorViewModel = ({ homeSearchCategory, keyword, tab }: Props): SearchDoctorViewModel => {
  const { showModal: showAppInstallModal } = useAppInstallRequiredModal();
  const { moveToPage, pageName } = useCustomRouter();
  const router = useRouter();
  const { items, category, hasNextPage, isLoading, fetchNextPage } = useInfiniteSearchNonF2fDoctors(
    {
      request: {
        keyword: keyword,
        sortType: DoctorSortType.Random,
        page: 1,
        limit: 10,
        doctorType: DoctorType.Doctor,
        telemedicineEligibleTargetType: TelemedicineEligibleTargetType.NightAndHolidayOnly,
      },
    },
    { enabled: EIntegratedSearchTabType.Doctor === tab && !!keyword }
  );

  const onClickDoctor = async (doctorPid: string) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: doctorPid,
        tab: EIntegratedSearchTabType.Doctor,
      },
    });

    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_DOCTOR_DETAIL_PAGE_AFTER_DIAGNOSIS_TARGET_CHECK, {
        doctorId: doctorPid,
        ...(EIntegratedSearchTabType.Doctor === tab
          ? category && {
              categoryId: Number(category.id),
              type:
                category.category === MedicalCategoryType.Treatment
                  ? EDeprecatedCategoryType.Symptom
                  : EDeprecatedCategoryType.Subject,
            }
          : homeSearchCategory && {
              type:
                homeSearchCategory.category === MedicalCategoryType.Treatment
                  ? EDeprecatedCategoryType.Symptom
                  : EDeprecatedCategoryType.Subject,
              categoryId: Number(homeSearchCategory.id),
            }),
      });
      return;
    } else {
      router.push(`${ROUTES.DOCTOR.DETAIL.path}/${doctorPid}`);
      return;
    }
  };

  const moveToCategoryListScreen = () => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_CATEGORY_SELECT);
      return;
    }
    showAppInstallModal({
      title: '비대면 진료는 앱에서 확인할 수 있어요.',
      body: '진료 뿐만 아니라\n약 처방, 의료진 상담까지 가능해요!',
      installButtonText: '지금 설치하기',
      closeButtonText: '닫기',
    });
  };

  const handleClickRequestButton = () => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName,
      payload: {
        position: ERequestButtonType.BOTTOM,
        keyword,
        section_name: 'integrate_search_doctor',
      },
    });

    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.appScreenName,
      params: { keyword },
    });
  };

  return {
    doctors: items,
    hasNextPage: !!hasNextPage,
    isLoading: isLoading,
    fetchNextPage: fetchNextPage,
    onClickDoctor,
    handleClickRequestButton,
    moveToCategoryListScreen,
    keyword,
  };
};

export default useSearchDoctorViewModel;
